import _ from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';

import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { useParentAddPlayerContact } from '../../../lib/services/parent.service';
import { ContactDuplicateField, ContactRelationship, IParentPlayerContact, Sport, SportInvolvement } from '../../../lib/types';
import { messages } from './parent-add-contact.messages';
import { ParentAddContact } from './parent-add-contact.component';
import { IParentAddContactFields } from './parent-add-contact.types';
import { readDuplicateFields } from '../../../lib/helpers/contact.helpers';
import { validationSchema } from './parent-add-contact.validation';

// Only the credentials parentAddPlayerContact actually refuses. Anything else it
// ever names has no field to land on, so it falls through to the general error.
const DUPLICATE_MESSAGE_BY_FIELD: Partial<Record<ContactDuplicateField, { id: string; defaultMessage: string }>> = {
  [ContactDuplicateField.Email]: messages.errEmailDuplicate,
  [ContactDuplicateField.Phone]: messages.errPhoneDuplicate,
};

interface IParentAddContactContainerProps {
  open: boolean;
  onClose: () => void;
  contacts: IParentPlayerContact[];
}

export const ParentAddContactContainer: React.FC<IParentAddContactContainerProps> = ({ open, onClose, contacts }) => {
  const [addContact] = useParentAddPlayerContact();
  const { enqueueSnackbar } = useSnackbar();

  // One option per athlete, not per row: a parent may hold several contact rows
  // on the same athlete, which would list them twice and defeat the pre-select.
  const playerOptions = useMemo(
    () => _.uniqBy(contacts, 'playerId').map((row) => ({ value: row.playerId, label: row.playerName })),
    [contacts],
  );

  // Pre-select when the parent only has one player — the picker is then a confirmation.
  const initialValues: IParentAddContactFields = useMemo(() => ({
    player: playerOptions.length === 1 ? playerOptions[0] : null,
    name: '',
    email: '',
    phone: '',
    relationship: null,
    sportInvolvement: null,
    sendEmail: true,
    sendSMS: false,
  }), [playerOptions]);

  const handleSubmit = useCallback(async (
    values: IParentAddContactFields,
    { setSubmitting, setStatus, setFieldError }: FormikHelpers<IParentAddContactFields>,
  ) => {
    setStatus(undefined);
    const playerId = values.player?.value as string;
    const selected = contacts.find((row) => row.playerId === playerId);
    const name = values.name.trim();

    try {
      await addContact({
        variables: {
          playerId,
          name,
          email: (values.email || '').trim() || null,
          phone: (values.phone || '').trim() || null,
          relationship: (values.relationship?.value as ContactRelationship) || null,
          // Hidden for non-default sports, so a value left over from a previous
          // player selection must not ride along with the send.
          sportInvolvement: selected?.sport === Sport.default
            ? (values.sportInvolvement?.value as SportInvolvement) || null
            : null,
        },
      });
      enqueueSnackbar(
        <IntlMsg {...messages.addContactSuccess} values={{ name, playerName: selected?.playerName || '' }} />,
        { variant: 'success' },
      );
      onClose();
    } catch (err) {
      console.error('parentAddPlayerContact failed:', err);
      // The refusal names the credential it rejected, so it lands on that field
      // rather than as a general failure the parent has to interpret.
      const mapped = readDuplicateFields(err).filter((field) => !!DUPLICATE_MESSAGE_BY_FIELD[field]);
      mapped.forEach((field) => setFieldError(field, DUPLICATE_MESSAGE_BY_FIELD[field]!.defaultMessage));
      if (!mapped.length) setStatus(<IntlMsg {...messages.addContactError} />);
    } finally {
      setSubmitting(false);
    }
  }, [addContact, contacts, enqueueSnackbar, onClose]);

  return (
    // key resets the fields each time the dialog reopens.
    <Formik<IParentAddContactFields>
      key={open ? 'open' : 'closed'}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {form => (
        <ParentAddContact
          open={open}
          onClose={onClose}
          contacts={contacts}
          playerOptions={playerOptions}
          form={form}
        />
      )}
    </Formik>
  );
};
